import { BLACK } from '@common/constants';
import { EdgeThickness } from '@common/enums/edge-thickness.enum';
import { ColorModel, ThicknessOption } from '@common/models';
import { MaterialModel, MaterialsAPIItem } from '@common/models/material.model';
import { COMING_SOON_IMAGE } from '@common/utils';

import {
    getColorsWithPrice,
    parseHexColor,
    setDefaultUrlToColors,
} from '../../pages/easy-estimator/components/scrollable-area/colors/colors.utils';

class MaterialService {
    // TODO: Refactor this to a method in a custom hook or a selector in a redux slice
    public static _parseAPIResponse(response: MaterialsAPIItem[]): MaterialModel[] {
        return response.map((apiItem) => {
            const { minPrice, maxPrice, avgPrice } = this._calculatePriceStats(apiItem, response.length);

            // we only want colors with a price, fix the image URL, do any other cleanup
            const fixedColors = this._getParsedMaterialColors(apiItem.colors);
            const qteGrpID = this._getQteGrpIDFromColors(fixedColors);

            const fixedMaterial: MaterialModel = {
                ...apiItem,
                id: `${qteGrpID}-${apiItem.description}`,
                minPrice,
                maxPrice,
                avgPrice,
                productLine: apiItem.description,
                qteGrpID,
                colors: fixedColors,
            };

            return fixedMaterial;
        });
    }

    private static _calculatePriceStats(
        apiMaterial: MaterialsAPIItem,
        apiMaterialsLength: number
    ): {
        minPrice: number | null;
        maxPrice: number | null;
        avgPrice: number | null;
    } {
        let minPrice: number | null = null;
        let maxPrice: number | null = null;
        let sumPrice = 0;

        apiMaterial.colors.forEach((color) => {
            if (!minPrice) {
                minPrice = color.price;
            }
            if (!maxPrice) {
                maxPrice = color.price;
            }

            if (color.price) {
                sumPrice += color.price;
                if (minPrice) {
                    minPrice = color.price < minPrice ? color.price : minPrice;
                }
                if (maxPrice) {
                    maxPrice = color.price > maxPrice ? color.price : maxPrice;
                }
            }
        });

        return { minPrice, maxPrice, avgPrice: sumPrice / apiMaterialsLength };
    }

    /**
     * The `qteGrpID` is selected from the thickness options inside colors array.
     * If available, the preferred `qteGrpID` is the same of the 3CM thickness option.
     * If not available, returns the first thickness of the first color.
     * Worst case scenario, no thickness is found at all, returns 0.
     */
    private static _getQteGrpIDFromColors(colors: ColorModel[]): number {
        const defaultThickness = EdgeThickness.threeCM;
        let qteGrpID: number | undefined;
        let fallbackQteGrpID = 0;
        for (const color of colors) {
            for (const option of color.thicknessOptions) {
                // First thickness of the first color
                if (!fallbackQteGrpID) {
                    fallbackQteGrpID = option.qteGrpID;
                }
                // Default thickness found, breaks loop
                if (option.thickness === defaultThickness) {
                    qteGrpID = option.qteGrpID;
                    break;
                }
            }
            // Default thickness found, breaks loop
            if (qteGrpID === undefined) {
                break;
            }
        }
        return qteGrpID || fallbackQteGrpID;
    }

    private static _getParsedMaterialColors(colors: ColorModel[]): ColorModel[] {
        return setDefaultUrlToColors(getColorsWithPrice(colors), COMING_SOON_IMAGE).map((color) => {
            const overlayColor = parseHexColor(color.overlayColor, BLACK);
            const cleanedColor: ColorModel = {
                ...color,
                overlayColor,
                thickness: this._fixThickness(color.thickness as EdgeThickness),
                thicknessOptions: color.thicknessOptions.map((thick) => {
                    const newThickness: ThicknessOption = {
                        ...thick,
                        thickness: this._fixThickness(thick.thickness),
                    };
                    return newThickness;
                }),
            };
            return cleanedColor;
        });
    }

    private static _fixThickness = (original: EdgeThickness): EdgeThickness => {
        if (original !== EdgeThickness.twoCM) {
            return EdgeThickness.threeCM;
        }
        return original;
    };
}

export default MaterialService;
